import { ChevronRightIcon, EnvelopeIcon, PencilIcon, PhoneIcon, TrashIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import { useState } from "react";

import Headline from "~/components/Headline";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { InvoiceRecipient } from "~/modules/billing/api/invoiceRecipient/invoiceRecipientTypes.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

interface Props {
	invoiceRecipient: InvoiceRecipient,
	onDeleteClick: () => void;
	onEditClick: () => void;
}

const InvoiceRecipientContactCard: React.FC<Props> = ({
	invoiceRecipient,
	onEditClick,
	onDeleteClick,
}) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	const {
		comment,
		displayName,
		invoiceByMail,
		invoiceEmail,
		invoiceHead,
		supportContactEmail,
		supportContactPhone,
		supportContactName,
	} = invoiceRecipient;
	return (
		<div
			className={clsx(
				"isolate col-span-1 transform-gpu divide-y divide-gray-200 rounded-lg shadow transition-transform duration-200",
				"bg-white",
				"hover:shadow-md",
			)}
		>
			<div className="group flex w-full items-center justify-between space-x-6 rounded-lg bg-white p-6">
				<div className="flex-1">
					<div className="flex items-center justify-between space-x-3">
						<Headline type="h5"
								  className="text-inherit">
							{displayName}
						</Headline>
						<div className="relative z-50">
							<ButtonWithPopoverDots theme="dark"
												   items={[
													   {
														   label: "Bearbeiten",
														   onClick: onEditClick,
														   icon: PencilIcon,
													   },
													   {
														   label: "Löschen",
														   onClick: onDeleteClick,
														   icon: TrashIcon,
													   },
												   ]} />
						</div>
					</div>

					{invoiceHead &&
						<div className="mt-1 flex flex-col items-start truncate whitespace-pre-wrap text-sm">
							{nl2br(invoiceHead)}
						</div>}
					<button type="button"
							className="mt-2 flex grow -translate-x-2 items-center text-sm font-light text-gray-500 hover:text-secondary-500"
							onClick={() => setIsExpanded(!isExpanded)}>
						<ChevronRightIcon className={clsx("size-5", isExpanded && "rotate-90")} />
						Weitere Details
					</button>
					{isExpanded && <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
						<span className="text-gray-600">Rechnungsversand per</span>
						<span>{invoiceByMail ? "E-Mail" : "Post"}</span>
						{invoiceByMail && <>
							<span className="text-gray-600">Rechnungsversand E-Mail</span>
							<span>{invoiceEmail || EMPTY_VALUE_PLACEHOLDER}</span>
						</>}
						<span className="text-gray-600">Support AP</span>
						<span>{supportContactName || EMPTY_VALUE_PLACEHOLDER}</span>
						<span className="text-gray-600">Support E-Mail</span>
						<span>{supportContactEmail || EMPTY_VALUE_PLACEHOLDER}</span>
						<span className="text-gray-600">Support Telefon</span>
						<span>{supportContactPhone || EMPTY_VALUE_PLACEHOLDER}</span>
						<span className={clsx("text-gray-600", comment && "col-span-2")}>Anmerkungen</span>
						<span className={comment ? "col-span-2":""}>{ comment? nl2br(comment): EMPTY_VALUE_PLACEHOLDER}</span>
					</div>}

				</div>
			</div>
			{(supportContactEmail || supportContactPhone) && (
				<div>
					<div className="-mt-px flex divide-x divide-gray-200">
						{supportContactEmail && (
							<div className="flex w-0 flex-1">
								<a
									href={`mailto:${supportContactEmail}`}
									className={clsx(
										"relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-4",
										"rounded-bl-lg border border-transparent",
										"text-sm font-semibold text-gray-700",
										"hover:bg-secondary-500 hover:text-white",
										"transition-all duration-200",
									)}
								>
									<EnvelopeIcon className="size-5 text-current"
												  aria-hidden="true" />
									E-Mail an Support
								</a>
							</div>
						)}
						{supportContactPhone && (
							<div className="-ml-px flex w-0 flex-1">
								<a
									href={`tel:${supportContactPhone}`}
									className={clsx(
										"relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-4",
										"rounded-br-lg border border-transparent",
										"text-sm font-semibold text-gray-900",
										"hover:bg-secondary-500 hover:text-white",
										"transition-all duration-200",
									)}
								>
									<PhoneIcon className="size-5 text-current"
											   aria-hidden="true" />
									Support anrufen
								</a>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default InvoiceRecipientContactCard;
